import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AirlinerSMSOTPVerificationPage = () => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`sms-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
    
    if (error) setError('');
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`sms-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    const code = verificationCode.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate verification (accept 123456 as valid for demo)
      if (code === '123456') {
        alert('SMS OTP verification successful!');
        navigate('/airliner/auth/complete-profile');
      } else {
        setError('Invalid SMS OTP code. Please try again.');
      }
    } catch (error) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      alert('SMS OTP code sent to your phone!');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      setError('Failed to resend SMS OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const smsIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
  );

  return (
    <AuthLayout
      title="SMS OTP Verification"
      subtitle="Enter the 6-digit SMS OTP code sent to your phone"
      icon={smsIcon}
      iconBgColor="bg-jet-yellow-100"
      iconColor="text-jet-yellow-600"
      showDemo={true}
      demoText="Use code 123456 to verify"
    >
      <form onSubmit={handleVerification} className="space-y-6">
        {/* SMS OTP Code Input */}
        <div>
          <label className="block text-sm font-medium text-jet-gray-700 mb-3 text-center">
            SMS OTP Code
          </label>
          <div className="flex justify-center space-x-3 mb-4">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                id={`sms-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none transition-colors ${
                  error 
                    ? 'border-jet-red-400 focus:border-jet-red-500' 
                    : 'border-jet-gray-300 focus:border-jet-yellow'
                }`}
              />
            ))}
          </div>
          
          {error && (
            <p className="text-sm text-jet-red-600 text-center mb-4">{error}</p>
          )}
        </div>

        {/* Submit Button */}
        <LoadingButton
          type="submit"
          loading={loading}
          disabled={verificationCode.join('').length !== 6}
          loadingText="Verifying..."
          className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900"
        >
          Verify SMS OTP
        </LoadingButton>

        {/* Resend Code */}
        <div className="text-center space-y-3">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendLoading || resendCooldown > 0}
            className={`text-sm transition-colors ${
              resendLoading || resendCooldown > 0
                ? 'text-jet-gray-400 cursor-not-allowed'
                : 'text-jet-yellow-600 hover:text-jet-yellow-700'
            }`}
          >
            {resendLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jet-gray-400 mr-2"></div>
                Sending...
              </div>
            ) : resendCooldown > 0 ? (
              `Resend SMS in ${resendCooldown}s`
            ) : (
              'Resend SMS OTP'
            )}
          </button>

          {/* Switch to Email OTP */}
          <div className="text-sm text-jet-gray-600">
            Didn't receive SMS?{' '}
            <button
              type="button"
              onClick={() => navigate('/airliner/auth/verify-otp')}
              className="text-jet-yellow-600 hover:text-jet-yellow-700 font-medium"
            >
              Try Email OTP instead
            </button>
          </div>
        </div>

        {/* Back to Registration */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <button
            type="button"
            onClick={() => navigate('/airliner/auth/register')}
            className="text-jet-gray-600 hover:text-jet-gray-900 text-sm transition-colors"
          >
            Back to Registration
          </button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default AirlinerSMSOTPVerificationPage;